import React, { useEffect, useMemo, useState } from "react";
import "../style/Cars.css";
import { supabase } from "../supabase/supabaseClient";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";

export default function CarsPage() {
  const { t } = useTranslation();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);

  const [brandFilter, setBrandFilter] = useState("Any");
  const [statusFilter, setStatusFilter] = useState("Any");
  const [priceFrom, setPriceFrom] = useState("");
  const [priceUntil, setPriceUntil] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCars = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from("cars")
          .select("*")
          .order("created_at", { ascending: false });
        if (error) throw error;
        setCars(data || []);
      } catch (error) {
        console.error("Error fetching cars:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("is-visible");
        });
      },
      { threshold: 0.08 }
    );
    document.querySelectorAll(".cars-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [cars]);

  const brands = useMemo(() => {
    const uniqueBrands = [...new Set(cars.map((car) => car.brand).filter(Boolean))];
    return ["Any", ...uniqueBrands];
  }, [cars]);

  const filteredCars = useMemo(() => {
    return cars.filter((car) => {
      const carPrice = Number(car.price || 0);
      const matchesBrand  = brandFilter === "Any" || car.brand === brandFilter;
      const matchesStatus = statusFilter === "Any" || car.status === statusFilter;
      const matchesFrom   = priceFrom === "" || carPrice >= Number(priceFrom);
      const matchesUntil  = priceUntil === "" || carPrice <= Number(priceUntil);
      return matchesBrand && matchesStatus && matchesFrom && matchesUntil;
    });
  }, [cars, brandFilter, statusFilter, priceFrom, priceUntil]);

  const translateStatus = (status) => {
    if (!status) return t("cars.available");
    const key = String(status).toLowerCase();
    if (key === "available") return t("cars.available");
    if (key === "reserved")  return t("cars.reserved");
    if (key === "sold")      return t("cars.sold");
    return status;
  };

  return (
    <div className="cars-page">
      <Header />

      {/* ── PAGE HERO ────────────────────────────────── */}
      <div className="cars-hero">
        <div className="cars-hero__content">
          <div className="cars-hero__accent" />
          <h1 className="cars-hero__title">{t("cars.pageTitle")}</h1>
          <p className="cars-hero__sub">{t("cars.pageSubtitle")}</p>
        </div>
      </div>

      <div className="cars-layout">
        {/* ── SIDEBAR FILTER ─────────────────────────── */}
        <aside className="cars-sidebar">
          <div className="filter-card">
            <div className="filter-group">
              <h3>{t("cars.vehicleCategory")}</h3>
              <div className="category-box">
                <span className="category-icon">🚗</span>
                <span>{t("cars.car")} ({cars.length})</span>
              </div>
            </div>

            <div className="filter-group">
              <h3>{t("cars.brand")}</h3>
              <select
                className="filter-select"
                value={brandFilter}
                onChange={(e) => setBrandFilter(e.target.value)}
              >
                {brands.map((brand) => (
                  <option key={brand} value={brand}>
                    {brand === "Any" ? t("cars.any") : brand}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-divider" />

            <div className="filter-group">
              <h3>{t("cars.status")}</h3>
              <select
                className="filter-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="Any">{t("cars.any")}</option>
                <option value="Available">{t("cars.available")}</option>
                <option value="Reserved">{t("cars.reserved")}</option>
                <option value="Sold">{t("cars.sold")}</option>
              </select>
            </div>

            <div className="filter-group">
              <h3>{t("cars.price")}</h3>
              <div className="price-row">
                <input
                  type="number"
                  className="price-input"
                  placeholder={t("cars.from")}
                  value={priceFrom}
                  onChange={(e) => setPriceFrom(e.target.value)}
                />
                <input
                  type="number"
                  className="price-input"
                  placeholder={t("cars.until")}
                  value={priceUntil}
                  onChange={(e) => setPriceUntil(e.target.value)}
                />
              </div>
            </div>
          </div>
        </aside>

        {/* ── LISTINGS ───────────────────────────────── */}
        <main className="cars-content">
          {loading ? (
            <p className="cars-message">{t("cars.loading")}</p>
          ) : filteredCars.length === 0 ? (
            <p className="cars-message">{t("cars.noCars")}</p>
          ) : (
            <div className="cars-list">
              {filteredCars.map((car, i) => (
                <div
                  className="listing-card cars-reveal"
                  key={car.id}
                  style={{ transitionDelay: `${Math.min(i, 4) * 0.08}s` }}
                >
                  <div className="listing-image-wrap">
                    <img
                      src={
                        car.main_image ||
                        car.images?.[0] ||
                        "/placeholder-car.jpg"
                      }
                      alt={car.car_name || t("cars.car")}
                      className="listing-image"
                    />
                  </div>

                  <div className="listing-content">
                    <div className="listing-top">
                      <h2>{car.car_name}</h2>
                      <span
                        className={`listing-badge ${String(car.status || "available")
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                      >
                        {translateStatus(car.status)}
                      </span>
                    </div>

                    <p className="listing-price">
                      €{Number(car.price || 0).toLocaleString()}
                    </p>

                    <p className="listing-meta">
                      {car.year ? `EZ ${car.year}` : "EZ -"} •{" "}
                      {car.technical_data?.Mileage || car.technical_data?.mileage || "-"} •{" "}
                      {car.technical_data?.Performance || car.technical_data?.performance || "-"} •{" "}
                      {car.technical_data?.["Fuel type"] || car.technical_data?.fuelType || "-"}
                    </p>

                    <div className="listing-divider" />

                    <div className="listing-actions">
                      <button
                        className="contact-btn"
                        type="button"
                        onClick={() => navigate(`/cars/${car.id}`)}
                      >
                        {t("cars.viewDetails")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
